import { logWarn, logError } from '../shared/utils/logger';
import { setTokens as persistTokens, clearTokens } from '../shared/utils/secureTokenStorage';
import { BASE_URL, normalizeTokens } from './client';

const parseJsonSafely = async (response) => {
    try {
        return await response.json();
    } catch (error) {
        return null;
    }
};

const fetchAuthJson = async (path, options) => {
    return fetch(`${BASE_URL}${path}`, options);
};

/**
 * RQ: { email: string, password: string }
 * RS: { status: 200, data: { user: { email: string } } }
 */
export async function login(payload) {
    const body = {
        password: payload.password,
        email: payload.email,
    };
    // Only include role if provided (for backwards compatibility)
    if (payload.role) {
        body.role = payload.role;
    }

    try {
        const response = await fetchAuthJson("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            // Handle org portal specific errors
            if (response.status === 403) {
                // User is not a member of this organization
                const error = new Error("You are not a member of this organization. Contact your administrator.");
                error.code = "NOT_ORG_MEMBER";
                throw error;
            }
            // Default: Invalid credentials (401)
            throw new Error("Invalid email or password.");
        }

        const data = await response.json();

        // DEBUG: Log full login response for troubleshooting
        console.log('[Auth] Login response:', {
            keys: Object.keys(data || {}),
            hasToken: !!data?.token,
            hasTokens: !!data?.tokens,
            hasAccessToken: !!data?.accessToken,
            hasAccess_token: !!data?.access_token,
            hasData: !!data?.data,
            dataKeys: data?.data ? Object.keys(data.data) : 'N/A'
        });

        // Handle different token response formats - check nested structures
        const tokenData =
            data?.token ||           // Direct: {token: "..."}
            data?.tokens ||          // Direct: {tokens: {...}}
            data?.access_token ||    // Direct snake_case: {access_token: "..."}
            data?.data?.token ||     // Nested: {data: {token: "..."}}
            data?.data?.tokens ||    // Nested: {data: {tokens: {...}}}
            data?.data?.access_token || // Nested snake_case: {data: {access_token: "..."}}
            data?.accessToken ||     // Direct camelCase: {accessToken: "..."}
            data?.data?.accessToken || // Nested camelCase: {data: {accessToken: "..."}}
            data?.data;              // Fallback: use data.data object

        if (tokenData) {
            // Get role from server response - server is authoritative
            const userRole = data?.data?.user?.role || data?.user?.role;

            if (!userRole) {
                throw new Error("Unable to sign in. Please try again.");
            }

            const normalizedTokens = normalizeTokens(tokenData);
            const tokensWithRole = {
                ...normalizedTokens,
                role: userRole,
            };

            // DEBUG: Log token storage for troubleshooting
            console.log('[Auth] Storing tokens after login:', {
                hasAccessToken: !!tokensWithRole.accessToken,
                tokenPreview: tokensWithRole.accessToken ? `${tokensWithRole.accessToken.substring(0, 20)}...` : 'none',
                role: userRole
            });

            // SECURITY: Use sessionStorage by default (clears on browser close)
            // Set rememberMe: true only if user explicitly requests it
            persistTokens(tokensWithRole, false);

            // Ensure role is in response for AuthScreen
            if (!data.user) data.user = {};
            data.user.role = userRole;
        } else {
            logWarn("Login response did not contain token data");
        }

        return { status: response.status, data };
    } catch (error) {
        logError("Login error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, password: string, referral_id?: string }
 * RS: { status: 200, data: { user: { email: string } } }
 */
export async function signup(payload) {
    // Get Rewardful referral ID if available
    const referralId = payload.referral_id || (typeof window !== 'undefined' && window.Rewardful?.referral) || null;

    const body = {
        email: payload.email,
        password: payload.password,
        username: payload.username || payload.email,
        membership: payload.membership || "Free",
        additionalProp1: {
            firstName: payload.firstName,
            lastName: payload.lastName,
            company: payload.company,
        },
        role: payload.role || "Lender"
    };

    // Include referral_id if present (for affiliate tracking)
    if (referralId) {
        body.referral_id = referralId;
    }
    try {
        const response = await fetchAuthJson("/api/auth/signup", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            // Check for specific error codes
            if (response.status === 409) {
                throw new Error("USER_EXISTS");
            }
            throw new Error("Unable to create account. Please try again.");
        }

        const data = await response.json();

        // Handle different token response formats - check nested structures
        const tokenData =
            data?.token ||           // Direct: {token: "..."}
            data?.tokens ||          // Direct: {tokens: {...}}
            data?.data?.token ||     // Nested: {data: {token: "..."}}
            data?.data?.tokens ||    // Nested: {data: {tokens: {...}}}
            data?.accessToken ||     // Direct: {accessToken: "..."}
            data?.data;              // Fallback: use data.data object

        if (tokenData) {
            // Include role in stored tokens for RBAC
            const userRole = data?.data?.user?.role || data?.user?.role || body.role;
            const tokensWithRole = {
                ...normalizeTokens(tokenData),
                role: userRole,
            };
            // SECURITY: Use sessionStorage by default (clears on browser close)
            // Set rememberMe: true only if user explicitly requests it
            persistTokens(tokensWithRole, false);

            // Ensure role is in response for AuthScreen
            if (!data.user) data.user = {};
            data.user.role = userRole;
        } else {
            logWarn("Signup response did not contain token data");
        }

        return { status: response.status, data };
    } catch (error) {
        logError("Signup error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string }
 * RS: { status: 200, data: { message: string } }
 */
export async function requestSignupCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/signup/request-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            throw new Error(errorData?.message || "Unable to send verification code.");
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Signup code request error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string }
 * RS: { status: 200, data: { verified: boolean } }
 */
export async function verifySignupCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/signup/verify-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email, code: payload.code })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Invalid or expired verification code.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Signup code verification error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string }
 * RS: { status: 200, data: { message: string, expiresIn: number } }
 */
export async function requestPasswordResetCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/request-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            throw new Error(errorData?.message || "Unable to send reset code.");
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset code request error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string }
 * RS: { status: 200, data: { verified: boolean } }
 */
export async function verifyPasswordResetCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/verify-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email, code: payload.code })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Invalid or expired reset code.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset code verification error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string, newPassword: string }
 * RS: { status: 200, data: { message: string } }
 */
export async function resetPassword(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/reset", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: payload.email,
                code: payload.code,
                newPassword: payload.newPassword
            })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Unable to reset password.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset error:", error);
        throw error;
    }
}

/**
 * Logout: Clear all stored authentication tokens
 */
export function logout() {
    clearTokens();
}
